import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db } from '@/lib/firebase';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { Loader2 } from 'lucide-react';

function ProductCard({ item, onClick }: { item: any; onClick: () => void }) {
  const { t } = useTranslation();
  const { i18n: i18nCtx } = useTranslation();
  const waLink = i18nCtx.language === 'es'
    ? 'https://wa.me/19862173604?text=Hola%2C%20quiero%20hacer%20un%20pedido.%20%2F%20Hello%2C%20I%20want%20to%20place%20an%20order.'
    : 'https://wa.me/19862173604?text=Hola%2C%20quiero%20hacer%20un%20pedido.%20%2F%20Hello%2C%20I%20want%20to%20place%20an%20order.';

  // Format price if needed
  const displayPrice = typeof item.price === 'number' ? `$${item.price.toFixed(2)}` : (item.price || '');

  return (
    <div 
      className="bg-[#111] rounded-2xl overflow-hidden border border-white/5 hover:border-[#F57F25]/40 transition-all duration-300 group flex flex-col cursor-pointer"
      onClick={onClick}
    >
      <div className="relative overflow-hidden h-48 bg-white/5">
        {item.image ? (
          <img
            src={item.image}
            alt={item.name}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20">
            <i className="ri-restaurant-line text-4xl"></i>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        {displayPrice && (
          <div className="absolute bottom-3 right-3 bg-[#F57F25] text-black font-black text-lg px-3 py-1 rounded-full">
            {displayPrice}
          </div>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-white font-black text-base uppercase tracking-wide mb-2">{item.name}</h3>
        <p className="text-white/55 text-sm leading-relaxed flex-1 mb-4">{item.description}</p>
        <a
          href={waLink}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 bg-[#F57F25] hover:bg-[#e06d15] text-black font-bold text-sm py-3 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
          onClick={(e) => e.stopPropagation()}
        >
          <i className="ri-whatsapp-line text-base"></i>
          {t('btn_order_whatsapp')}
        </a>
      </div>
    </div>
  );
}

export default function MenuSection() {
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [activeCategory, setActiveCategory] = useState('');
  const [categories, setCategories] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    // Fetch Categories
    const qCat = query(collection(db, 'categories'));
    const unsubCat = onSnapshot(qCat, (snap) => {
      const cats = snap.docs.map(doc => {
        const data = doc.data();
        const name = data.name ? (typeof data.name === 'string' ? data.name : (data.name[i18n.language] || data.name.en || '')) : (data.label || data.title || '');
        return { id: doc.id, ...data, label: name, name: name };
      });
      cats.sort((a, b) => (a.order || 0) - (b.order || 0));
      setCategories(cats);
      
      setActiveCategory(prev => {
        if (!prev && cats.length > 0) return cats[0].id;
        if (prev && !cats.find(c => c.id === prev) && cats.length > 0) return cats[0].id;
        return prev;
      });
    });

    // Fetch Menu Items
    const qItems = query(collection(db, 'menu_items'));
    const unsubItems = onSnapshot(qItems, (snap) => {
      setItems(snap.docs.map(doc => {
        const data = doc.data();
        const name = data.name ? (typeof data.name === 'string' ? data.name : (data.name[i18n.language] || data.name.en || '')) : '';
        const description = data.description ? (typeof data.description === 'string' ? data.description : (data.description[i18n.language] || data.description.en || '')) : '';
        return { id: doc.id, ...data, name, description };
      }));
      setLoading(false);
    });

    return () => {
      unsubCat();
      unsubItems();
    };
  }, [i18n.language]);

  const filtered = items.filter((item) => item.categoryId === activeCategory);

  return (
    <section id="menu" className="bg-black py-20 md:py-28 relative">
      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-[#F57F25] text-xs font-bold tracking-widest uppercase mb-4">
            <div className="w-8 h-px bg-[#F57F25]"></div>
            {t('menu_eyebrow')}
            <div className="w-8 h-px bg-[#F57F25]"></div>
          </div>
          <h2 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tight mb-4">
            {t('menu_headline')} <span className="text-[#F57F25]">{t('menu_headline_accent')}</span>
          </h2>
          <p className="text-white/50 text-base max-w-xl mx-auto">
            {t('menu_sub')}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="text-[#F57F25] animate-spin" size={48} />
          </div>
        ) : (
          <>
            {/* Category Tabs */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {categories.map((cat) => (
                <button
                  key={`${cat.id}-${i18n.language}`}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeCategory === cat.id
                      ? 'bg-[#F57F25] text-black'
                      : 'bg-[#111] border border-white/10 text-white/70 hover:border-[#F57F25]/40 hover:text-white'
                  }`}
                >
                  {cat.icon && (
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-sm`}></i>
                    </div>
                  )}
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Product Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filtered.map((item) => (
                <ProductCard key={item.id} item={item} onClick={() => setSelectedItem(item)} />
              ))}
            </div>
            
            {filtered.length === 0 && (
              <div className="text-center py-12 text-white/40">
                {t('no_items_found', 'No items found in this category.')}
              </div>
            )}
          </>
        )}

        {/* Modal */}
        {selectedItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setSelectedItem(null)}>
            <div 
              className="bg-[#111] border border-white/10 rounded-3xl w-full max-w-3xl overflow-hidden relative animate-in zoom-in-95 duration-200 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <button 
                onClick={() => setSelectedItem(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-black/50 hover:bg-[#F57F25] text-white hover:text-black rounded-full flex items-center justify-center transition-colors z-10"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>

              <div className="flex flex-col md:flex-row h-full max-h-[85vh] overflow-y-auto custom-scrollbar">
                {/* Image */}
                <div className="w-full md:w-1/2 h-64 md:h-auto min-h-[300px] relative bg-black shrink-0">
                  {selectedItem.image ? (
                    <img src={selectedItem.image} alt={selectedItem.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/20">
                      <i className="ri-restaurant-line text-6xl"></i>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent md:hidden"></div>
                </div>
                
                {/* Content */}
                <div className="p-8 w-full md:w-1/2 flex flex-col bg-[#111]">
                  <h3 className="text-white font-black text-3xl uppercase tracking-wide mb-2 leading-tight">{selectedItem.name}</h3>
                  {typeof selectedItem.price === 'number' && (
                    <div className="text-[#F57F25] font-black text-2xl mb-6">${selectedItem.price.toFixed(2)}</div>
                  )}
                  <p className="text-white/70 text-base leading-relaxed mb-8 flex-1">{selectedItem.description}</p>
                  
                  <a
                    href={`https://wa.me/19862173604?text=Hola%2C%20quiero%20hacer%20un%20pedido%20de%20${encodeURIComponent(selectedItem.name)}.%20%2F%20Hello%2C%20I%20want%20to%20order%20${encodeURIComponent(selectedItem.name)}.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-[#F57F25] hover:bg-[#e06d15] text-black font-black text-base py-4 px-8 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap mt-auto shadow-xl shadow-[#F57F25]/20 hover:scale-105 active:scale-95"
                  >
                    <i className="ri-whatsapp-line text-2xl"></i>
                    {t('btn_order_whatsapp')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bottom CTA */}
        <div className="text-center mt-14 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => navigate('/menu')}
            className="inline-flex items-center gap-2 bg-[#F57F25] hover:bg-[#e06d15] text-black font-bold text-base px-8 py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-restaurant-line text-lg"></i>
            </div>
            {t('btn_view_full_menu')}
          </button>
          <a
            href="https://wa.me/19862173604?text=Hola%2C%20quiero%20hacer%20un%20pedido.%20%2F%20Hello%2C%20I%20want%20to%20place%20an%20order."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold text-base px-8 py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-whatsapp-line text-lg"></i>
            </div>
            {t('btn_chat_whatsapp')}
          </a>
        </div>
      </div>
    </section>
  );
}
